import { ReactNode } from 'react';
import SelectBoxField from '@/components/select/SelectBoxField';
import { SingleSelectBoxProps } from '@/components/select/selectBoxTypes';
import useSingleSelectBox from '@/components/select/useSingleSelectBox';
import { Listbox } from '@headlessui/react';
import { useQuery } from '@tanstack/react-query';

const SingleSelectBox = <T extends { id: string; name: string; disabled?: boolean }>({
  name,
  value,
  label,
  hideLabel,
  deselectLabel,
  placeholder,
  queryKey = name ? [name] : [],
  queryFn,
  onSelect,
  allowEmptySelection = true,
  requireSelection,
  disabled = false,
  defaultValue = requireSelection ? 'first' : 'none',
  itemRender,
  hideIfEmpty,
}: SingleSelectBoxProps<T>): ReactNode => {
  const { isLoading, data } = useQuery({ queryKey, queryFn });
  const items = data ?? [];
  const selection = useSingleSelectBox<T>(data, { value, onSelect }, defaultValue);

  if (hideIfEmpty && items.length === 0) return null;

  const displayText = isLoading
    ? 'Loading ...'
    : selection.selected
      ? itemRender
        ? itemRender(selection.selected)
        : selection.selected.name
      : deselectLabel || placeholder || '';

  return (
    <Listbox
      value={selection.selected}
      onChange={selection.handleChange}
      disabled={disabled}
      multiple={false}
    >
      <SelectBoxField<T>
        label={label}
        hideLabel={hideLabel}
        deselectLabel={deselectLabel}
        displayText={displayText}
        itemRender={itemRender}
        items={items}
        allowEmptySelection={allowEmptySelection}
        requireSelection={requireSelection}
        hasNoSelection={selection.selected === null}
        onDeselect={selection.handleDeselect}
      />
    </Listbox>
  );
};

export default SingleSelectBox;
